import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Check, Plus, X } from "lucide-react";
import { type ClipboardEvent, type FormEvent, useCallback, useState } from "react";

import {
  ApiError,
  submitTeamInvites,
  type TeamInviteFailure,
  type TeamInviteResponse,
  type TeamInviteRole,
  type TeamInviteSuccess,
} from "@/api/client";
import { LoadingOverlay } from "@/components/BrandedSpinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormError } from "@/components/ui/form-error";
import { useToast } from "@/components/ui/toast";

interface InviteRow {
  email: string;
  role: TeamInviteRole;
}

const ROLE_LABEL: Record<TeamInviteRole, string> = {
  po: "Program Owner",
  bdm: "Business Development Manager",
};

/**
 * CHP (po-dashboard): split a bulk-pasted blob into individual addresses.
 * POs invite teams in batches, so the email field accepts comma- OR
 * newline-separated lists (also tabs / semicolons, which spreadsheet and
 * mail-client copies emit). Returns trimmed, non-empty tokens in order;
 * de-duplication and validation stay where they already were (filledRows).
 */
export function splitEmails(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * CHP-545: case-insensitive intra-form duplicate detection. The backend
 * de-duplicates (first row wins), but silently dropping a row leaves the PO
 * thinking everyone was invited — so the form surfaces the duplicates and
 * blocks submission until they're resolved (matching the server's lower-cased
 * normalisation). Returns the normalised addresses that appear more than once,
 * in first-seen order; only filled (`@`-containing) rows count.
 */
export function findDuplicateEmails(emails: string[]): string[] {
  const seen = new Set<string>();
  const dups: string[] = [];
  for (const raw of emails) {
    const e = raw.trim().toLowerCase();
    if (!e.includes("@")) continue;
    if (seen.has(e)) {
      if (!dups.includes(e)) dups.push(e);
    } else {
      seen.add(e);
    }
  }
  return dups;
}

export interface TeamInviteFormProps {
  token: string;
  /** The Channel Partner organisation name shown in the joining copy. */
  orgName: string | null;
  /**
   * The organisation's id. CHP-869 removed the "(ID: …)" suffix that used to
   * render next to the org name in the joining copy, so this is accepted for
   * back-compat with existing callers but no longer displayed.
   */
  orgId?: string | null;
  /**
   * Optional completion hook. When supplied (e.g. the dashboard modal), it
   * replaces the default redirect-to-dashboard on full success and on the
   * "Go to my dashboard" button — the modal closes itself instead of
   * navigating away. Omitted on the standalone /dashboard/partner/team route
   * and the onboarding Registration Complete page, which keep the redirect.
   */
  onDone?: () => void;
}

/**
 * CHP-295: shared team-invite form — per-invitee email + access level rows.
 * Used by the onboarding Registration Complete page (embedded, skippable)
 * and the PO dashboard's Invite your team page; the design review requires
 * both surfaces to be the same flow.
 *
 * After a fully-successful send (CHP-792) it fires a toast reminding the PO to
 * tell recipients to check junk mail (links expire in 3 days) and returns to the
 * dashboard; partial failures hold the page inline so the PO can read the
 * per-row reasons.
 */
export function TeamInviteForm({ token, orgName, onDone }: TeamInviteFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rows, setRows] = useState<InviteRow[]>([{ email: "", role: "bdm" }]);
  const [topError, setTopError] = useState<string | null>(null);
  // Only PARTIAL-failure results land here for the inline per-row breakdown; an
  // all-success send toasts + returns instead of rendering a confirmation screen.
  const [lastResult, setLastResult] = useState<TeamInviteResponse | null>(null);

  const filledRows = rows.filter((r) => r.email.trim().includes("@"));
  // CHP-545: surface intra-form duplicates so a row isn't silently de-duped.
  const dupEmails = findDuplicateEmails(rows.map((r) => r.email));
  const dupMessage =
    dupEmails.length > 0
      ? `Each invitee should appear once — remove the duplicate ${
          dupEmails.length === 1 ? "address" : "addresses"
        }: ${dupEmails.join(", ")}.`
      : null;

  // Completion: the standalone surfaces redirect to the dashboard; the Sheet
  // closes itself via onDone. Centralised so the toast-then-return path and the
  // partial-failure "Done"/"Go to my dashboard" button take the same exit.
  const finish = useCallback(() => {
    if (onDone) {
      onDone();
      return;
    }
    void navigate({ to: "/dashboard" });
  }, [onDone, navigate]);

  const submit = useMutation({
    mutationFn: () =>
      submitTeamInvites(token, {
        invites: filledRows.map((r) => ({ email: r.email.trim().toLowerCase(), role: r.role })),
      }),
    onSuccess: (resp) => {
      // CHP-792: an all-success send fires a single reminder toast and returns
      // to the dashboard — no inline confirmation screen, no auto-redirect
      // timer. A PARTIAL failure holds the page inline so the PO can read the
      // per-row reasons (we do NOT toast-and-dismiss those).
      if (resp.errors.length === 0) {
        toast({
          description:
            "Invites sent. Please remind recipients to check their junk mail; links expire in 3 days.",
          variant: "success",
        });
        setRows([{ email: "", role: "bdm" }]);
        finish();
        return;
      }
      setLastResult(resp);
      setRows([{ email: "", role: "bdm" }]);
    },
    onError: (err) => {
      setLastResult(null);
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setTopError("Only your organisation's Program Owner can invite team members.");
          return;
        }
        if (err.status === 422) {
          setTopError("One or more email addresses look invalid. Fix them and resubmit.");
          return;
        }
        if (err.status === 409) {
          setTopError(
            "Your account isn't linked to an organisation yet. Complete your own onboarding before inviting your team.",
          );
          return;
        }
        if (err.status === 429) {
          setTopError("Too many invitations in a short window. Try again in a minute.");
          return;
        }
      }
      setTopError("Couldn't send the invitations. Try again.");
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTopError(null);
    if (filledRows.length === 0) {
      setTopError("Add at least one email address.");
      return;
    }
    // The duplicate warning shows live + disables the button; guard the
    // Enter-key submit path too (CHP-545).
    if (dupMessage) return;
    submit.mutate();
  }

  function setRow(index: number, patch: Partial<InviteRow>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  /**
   * Bulk paste: a comma- or newline-separated blob dropped into one email
   * field expands into one row per address (each defaulting to BDM, the
   * conservative least-privilege role — POs flip the per-row toggle as
   * needed). The first parsed address fills the pasted row; the rest append.
   * A single-address paste falls through to the native input handler so the
   * normal type/paste behaviour is untouched.
   */
  function onEmailPaste(index: number, e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text");
    const emails = splitEmails(text);
    if (emails.length <= 1) return; // let the browser handle a plain paste
    e.preventDefault();
    setRows((prev) => {
      const next = prev.map((r, i) => (i === index ? { ...r, email: emails[0]! } : r));
      const extra: InviteRow[] = emails.slice(1).map((email) => ({ email, role: "bdm" }));
      next.splice(index + 1, 0, ...extra);
      return next;
    });
  }

  // CHP-792: only a PARTIAL-failure send reaches this inline screen — an
  // all-success send already toasted and returned. The PO must read the per-row
  // reasons before leaving, so this is held (not toast-and-dismissed).
  if (lastResult) {
    return (
      <div className="space-y-4" data-testid="team-invite-result">
        <p className="text-sm font-medium" role="status">
          Some invites were sent — review the rows below.
        </p>
        {lastResult.invited.length > 0 && <InvitedList items={lastResult.invited} />}
        {lastResult.errors.length > 0 && <ErrorList items={lastResult.errors} />}
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={() => setLastResult(null)}>
            Invite more
          </Button>
          <Button type="button" onClick={finish}>
            {onDone ? "Done" : "Go to my dashboard"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-busy={submit.isPending}
      noValidate
      className="relative space-y-4"
    >
      {/* CHP-314: invite dispatch is slow (one Authentik user + link + email
          per row); show the branded spinner over the form while it runs. */}
      {submit.isPending ? <LoadingOverlay label="Sending invitations…" /> : null}
      {/* CHP-578: CHP-295 signed-off verbatim intro. {orgName} fills the
          [Channel Partner name] placeholder; falls back to "your organisation"
          when there's no org context (e.g. a launch site without an org). */}
      <p className="text-sm text-muted-foreground">
        New team members you invite will join{" "}
        {orgName ? <strong>{orgName}</strong> : "your organisation"}&apos;s profile automatically.
        They will receive a secure link to set their password.
      </p>
      {/* CHP-792: role descriptions as plain text — no bordered/accent callout. */}
      <div className="space-y-1 text-sm text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">Program Owner:</span> Full access to
          individual and organisation data and user management.
        </p>
        <p>
          <span className="font-medium text-foreground">BDM:</span> View-only access to individual
          data.
        </p>
      </div>

      {/* CHP-318: each invitee is a stacked card — email full-width on top,
          access level + remove below. The form renders in both a narrow
          onboarding card and a wide dashboard card, and Tailwind's viewport
          breakpoints can't tell those apart, so stacking (rather than an inline
          row) keeps the email from being squeezed by the access-level select. */}
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="space-y-2 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor={`invite-email-${i}`} className="text-xs">
                Email address
              </Label>
              <Input
                id={`invite-email-${i}`}
                type="email"
                value={row.email}
                placeholder="name@example.com"
                aria-invalid={dupEmails.includes(row.email.trim().toLowerCase())}
                onChange={(e) => setRow(i, { email: e.target.value })}
                onPaste={(e) => onEmailPaste(i, e)}
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label htmlFor={`invite-role-${i}`} className="text-xs">
                  Access level
                </Label>
                <Select
                  value={row.role}
                  onValueChange={(v) => setRow(i, { role: v as TeamInviteRole })}
                >
                  <SelectTrigger
                    id={`invite-role-${i}`}
                    aria-label={`Access level for row ${i + 1}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bdm">{ROLE_LABEL.bdm}</SelectItem>
                    <SelectItem value="po">{ROLE_LABEL.po}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={rows.length === 1}
                aria-label={`Remove row ${i + 1}`}
                onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {dupMessage && <FormError>{dupMessage}</FormError>}
      {topError && <FormError>{topError}</FormError>}

      {/* CHP-318: row-add (left) + dispatch (right) share one divider row so the
          two actions read as a related pair instead of floating apart. */}
      <div className="flex items-center justify-between border-t pt-4">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setRows((prev) => [...prev, { email: "", role: "bdm" }])}
        >
          <Plus className="h-4 w-4" aria-hidden /> Add team member
        </Button>
        <Button
          type="submit"
          disabled={filledRows.length === 0 || dupEmails.length > 0}
          loading={submit.isPending}
          loadingLabel="Sending…"
        >
          Send invites
        </Button>
      </div>
    </form>
  );
}

const REASON_LABEL: Record<TeamInviteFailure["reason"], string> = {
  duplicate: "Already invited or signed up",
  authentik_error: "Identity service rejected the invite — retry in a minute",
  unexpected: "Something went wrong — please retry",
};

function InvitedList({ items }: { items: TeamInviteSuccess[] }) {
  return (
    <div>
      <p className="text-sm font-medium text-foreground">Invited ({items.length})</p>
      <ul className="mt-2 space-y-1 text-sm">
        {items.map((s) => (
          <li key={s.email} className="flex items-center gap-1.5 text-muted-foreground">
            <Check className="h-4 w-4 shrink-0 text-success" aria-hidden />
            <span>
              {s.email} <span className="text-xs">({ROLE_LABEL[s.role]})</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ErrorList({ items }: { items: TeamInviteFailure[] }) {
  return (
    <div>
      <p className="text-sm font-medium text-destructive">Not sent ({items.length})</p>
      <ul className="mt-2 space-y-1 text-sm">
        {items.map((f) => (
          <li key={f.email} className="flex items-center gap-1.5">
            <X className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
            <span>
              <span className="text-destructive">{f.email}</span>
              <span className="ml-2 text-xs text-muted-foreground">— {REASON_LABEL[f.reason]}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
